import styled from "@emotion/styled";
import { parseGid } from "@shopify/hydrogen-react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  FakeLabel,
  FlexRow,
  IconArrow,
  LoadingSpinner,
  RequiredText,
  Stack,
  Text,
  UnexpectedError,
} from "atoms";
import { FileInput, useFileUpload } from "features/images";
import { paths } from "utils/paths";
import { removeFileExt } from "utils/text";
import { useCreateProduct } from "../../api/useCreateProduct";
import type { CreateStepProps } from "../../types";
import { ProductWizardTemplate } from "../ProductWizardTemplate";

function CreateStepUploadAsset({
  artistId,
  productTemplate,
  moveBack,
}: CreateStepProps) {
  const navigate = useNavigate();
  const uploadMutation = useFileUpload();

  const productMutation = useCreateProduct();

  // Once a file is selected, upload it
  const onChange = (file: File) =>
    uploadMutation.mutate(
      { file },
      {
        onSuccess: ({ url }) => {
          // On successful upload, create the new product
          productMutation.mutate(
            {
              artistId,
              templateId: productTemplate.id,
              assetUrl: url,
              productName: removeFileExt(file.name),
            },
            {
              onSuccess: ({ shopifyProductId }) => {
                // On succesful product creation, navigate to Product Wizard to configure it
                navigate(
                  paths.artistShopProductEdit(
                    artistId,
                    parseGid(shopifyProductId).id,
                  ),
                );
              },
            },
          );
        },
      },
    );

  // Merge states of both mutations to simplify UI logic
  const isLoading = uploadMutation.isLoading || productMutation.isLoading;
  const isError = uploadMutation.isError || productMutation.isError;
  const error = uploadMutation.error ?? productMutation.error;

  const isDigitalDownload = productTemplate.productType === "digital download";
  const subtitle = isDigitalDownload
    ? "Upload Your Asset"
    : "Upload Your Artwork";

  // TODO: Replace with constant when merged
  // 100mb = mb * kb * b
  const MAX_UPLOAD_SIZE = 100 * 1000 * 1000;

  return (
    <ProductWizardTemplate
      title="Product Builder"
      subtitle={subtitle}
      moveBack={moveBack}
    >
      <Stack gap="lg">
        <FlexRow justifyContent="center" style={{ marginBlock: -20 }}>
          <Button size="xxs" variant="text" onClick={moveBack}>
            <small>
              <IconArrow dir="l" />
            </small>{" "}
            Choose a Different Product
          </Button>
        </FlexRow>

        <Container width="xs">
          <Box small>
            <Stack gap="md">
              <FileInputWrapper>
                <Stack gap="sm">
                  <Stack gap="xs">
                    <FakeLabel as={Stack}>
                      {isDigitalDownload ? (
                        <Stack>
                          <Text size="sm" bold>
                            Digital Download File
                            <RequiredText />
                          </Text>
                          <Text size="xs">
                            Upload the asset your customers will receive after
                            purchase.
                          </Text>
                        </Stack>
                      ) : (
                        <Stack>
                          <Text size="sm" bold>
                            Upload Artwork
                            <RequiredText />
                          </Text>

                          <Stack gap="xxs">
                            <Stack gap="xxxs">
                              <Text size="xxs">
                                <strong>Accepted files types:</strong> JPG, PNG
                              </Text>
                              <Text size="xxs">
                                <strong>Recommended dimensions:</strong> 4000px
                                to 6000px
                              </Text>
                            </Stack>
                          </Stack>
                        </Stack>
                      )}
                    </FakeLabel>
                  </Stack>
                  <FileInput
                    onChange={onChange}
                    accept={isDigitalDownload ? "document" : "image"}
                    maxFileSize={MAX_UPLOAD_SIZE}
                  />
                </Stack>
              </FileInputWrapper>
            </Stack>
          </Box>
        </Container>

        {isLoading && <LoadingSpinner text="Uploading" />}

        {isError && <UnexpectedError error={error} />}
      </Stack>
    </ProductWizardTemplate>
  );
}

const FileInputWrapper = styled.div({
  "&>div": {
    gap: 10,
  },
});

export { CreateStepUploadAsset };
